import { Center, Slider, Group, Button } from "@mantine/core"
import Cropper, { Area } from "react-easy-crop"
import { DefaultModal } from "@components/default-modal"
import { useTranslation } from "react-i18next"

interface Props {
  opened: boolean
  imageSrc: string | null
  rawFile?: File | null
  aspect?: number
  onConfirm: (file: File) => void
  onCancel: () => void
}

export function ImageCropper({
  opened,
  imageSrc,
  rawFile,
  aspect = 1,
  onConfirm,
  onCancel,
}: Props) {
  const [crop, setCrop] = React.useState({ x: 0, y: 0 })
  const [zoom, setZoom] = React.useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(
    null
  )

  const createImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.addEventListener("load", () => resolve(img))
      img.addEventListener("error", (e) => reject(e))
      img.setAttribute("crossOrigin", "anonymous")
      img.src = url
    })

  const getCroppedImg = async (
    imageSrcParam: string,
    pixelCrop: Area
  ): Promise<Blob | null> => {
    try {
      const image = await createImage(imageSrcParam)
      const canvas = document.createElement("canvas")
      canvas.width = Math.max(1, Math.round(pixelCrop.width))
      canvas.height = Math.max(1, Math.round(pixelCrop.height))
      const ctx = canvas.getContext("2d")
      if (!ctx) return null

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      )

      return await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9)
      )
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const onCropComplete = React.useCallback(
    (_: Area, croppedAreaPixelsParam: Area) => {
      setCroppedAreaPixels(croppedAreaPixelsParam)
    },
    []
  )

  const handleConfirm = React.useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels)
    if (!blob) {
      onCancel()
      return
    }

    const originalName = rawFile?.name ?? "profile"
    const mime = blob.type || "image/jpeg"
    const extMap: Record<string, string> = {
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/png": ".png",
      "image/webp": ".webp",
    }
    const ext = extMap[mime] ?? ".jpg"
    const baseName = originalName.replace(/\.[^/.]+$/, "")
    const fileName = baseName + ext
    const file = new File([blob], fileName, { type: mime })

    onConfirm(file)
  }, [imageSrc, croppedAreaPixels, rawFile, onConfirm, onCancel])

  const { t } = useTranslation()

  return (
    <DefaultModal
      opened={opened}
      onClose={onCancel}
      title={t("CropImage")}
      size="lg"
      centered
    >
      <div style={{ position: "relative", height: 400, background: "#333" }}>
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        )}
      </div>
      <Center mt="sm">
        <div style={{ width: "80%" }}>
          <Slider min={1} max={3} step={0.01} value={zoom} onChange={setZoom} />
        </div>
      </Center>
      <Group mt="md">
        <Button variant="default" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button onClick={handleConfirm}>{t("Confirm")}</Button>
      </Group>
    </DefaultModal>
  )
}

export default ImageCropper
